import { getFormattedPrice } from 'Components/ContractGenerator/shared/helpers/priceHelpers';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Badge from 'ViatoUi/Shared/DataDisplay/Badge';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Loader from 'ViatoUi/Shared/Loader';
import Paper from 'ViatoUi/Shared/Surfaces/Paper';
import { Headline, Paragraph } from 'ViatoUi/Shared/Typo';
import Button from 'ViatoUi/Web/FormElements/Buttons/Button';

import styles from './Card.module.scss';

const Card = ({ headline, children, hasPriceFooter, priceFooterSettings, hasButton, buttonSettings, isSmall }) => {
	const { t } = useTranslation('contractGenerator');

	const getBadgeText = (discountType, discountValue, defaultPrice) => {
		if (discountType === 'percentage') {
			if (discountValue > 100) {
				return 100;
			}

			return discountValue;
		}

		if (discountValue > defaultPrice * 100) {
			return defaultPrice;
		}

		return discountValue / 100;
	};

	const getPriceContent = (defaultPrice, discountedPrice, discountValue, discountType) => {
		if (defaultPrice === null || discountedPrice === null) {
			return <Loader iconOnly />;
		}

		if (discountValue > 0) {
			return (
				<FlexBox alignItems='baseline'>
					<Paragraph color='extraDarkSmoke' font='large' lineThrough>
						{getFormattedPrice(defaultPrice)}&nbsp;€*
					</Paragraph>
					<Spacer width='s' />
					<Headline type='h4'>{getFormattedPrice(discountedPrice)}&nbsp;€*</Headline>
					<Spacer width='s' />
					<Badge
						label={
							discountType === 'percentage'
								? `-${getBadgeText(discountType, discountValue, defaultPrice)}%`
								: `-${getFormattedPrice(getBadgeText(discountType, discountValue, defaultPrice))}€*`
						}
						size='s'
						type='error'
						inline
					/>
				</FlexBox>
			);
		}

		return <Headline type='h4'>{getFormattedPrice(defaultPrice)}&nbsp;€*</Headline>;
	};

	return (
		<Paper
			className={styles.card}
			data={{ 'data-has-button': hasButton, 'data-is-small': isSmall }}
			elevation='strong'
			roundness='strong'
			spacing='xl'
		>
			<FlexBox alignItems='flex-start' className={styles.wrapper} justifyContent='space-between'>
				<div className={styles.left}>
					{headline !== null && (
						<>
							<Headline type='h2'>{headline}</Headline>
							<Spacer height='l' />
						</>
					)}

					{children}
					{hasPriceFooter === true && (
						<>
							<Spacer height='l' />
							<FlexBox className={styles.priceFooter}>
								{priceFooterSettings.feePrice > 0 || priceFooterSettings.feePrice === null ? (
									<>
										<div>
											{getPriceContent(
												priceFooterSettings.feePrice,
												priceFooterSettings.feePriceDiscounted,
												priceFooterSettings.feeDiscountValue,
												priceFooterSettings.feeDiscountType
											)}
											<Paragraph color='silver' font='large'>
												{t('layouts.card.setup')}
											</Paragraph>
										</div>
										<Spacer width='xl' />
									</>
								) : (
									<></>
								)}
								{priceFooterSettings.price > 0 || priceFooterSettings.price === null ? (
									<div>
										{getPriceContent(
											priceFooterSettings.price,
											priceFooterSettings.priceDiscounted,
											priceFooterSettings.discountValue,
											priceFooterSettings.discountType
										)}
										<Paragraph color='silver' font='large'>
											{priceFooterSettings.isSinglePayment ? t('layouts.card.once') : t('layouts.card.monthly')}
										</Paragraph>
									</div>
								) : (
									<></>
								)}
							</FlexBox>
						</>
					)}
				</div>
				{hasButton === true && (
					<Button className={styles.button} type={buttonSettings.type} onClick={() => buttonSettings.onClick()}>
						{buttonSettings.text}
					</Button>
				)}
			</FlexBox>
		</Paper>
	);
};

Card.propTypes = {
	headline: PropTypes.string,
	children: PropTypes.node.isRequired,
	hasPriceFooter: PropTypes.bool,
	priceFooterSettings: PropTypes.object,
	hasButton: PropTypes.bool,
	buttonSettings: PropTypes.object,
	isSmall: PropTypes.bool
};

Card.defaultProps = {
	headline: null,
	hasPriceFooter: false,
	priceFooterSettings: {},
	hasButton: false,
	buttonSettings: {},
	isSmall: false
};

export default Card;
